import { AlertContext } from "@minervaproject/fds";
import jwt from "jsonwebtoken";
import { useContext, useEffect, useState } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { useParams } from "react-router-dom";

import { MonitoringClassDashboard } from "./class_dashboard";
import { MonitoringClassUserDashboard } from "./class_user_dashboard";

import { ENVIRONMENT } from "config";
import { FlightAPI } from "contexts/flight_api_context";
import { ForumAPI } from "contexts/forum_api_context";
import { MonitoringStoreType, getMonitoringReduxPubsubClient } from "pubsub/client";
import { FlagNameType } from "shared/types";
import { isFlagActiveForClass } from "shared/utils/flag";
import { redirectToLogin, redirectToNotFound } from "utils/classroom";
import { getAuthToken } from "utils/cookie";

function MonitoringWrapper() {
  const { classId, userId } = useParams();
  const { addAlert } = useContext(AlertContext);

  const [store, setStore] = useState<MonitoringStoreType>();

  const flightAuthToken = getAuthToken();

  useEffect(() => {
    async function initializeReduxPubsub() {
      if (!classId) {
        redirectToNotFound();
        return;
      }

      const flightAuth = jwt.decode(flightAuthToken);
      if (!flightAuth || typeof flightAuth !== "object") {
        redirectToLogin();
        return;
      }
      const currentUserId = `${flightAuth.userId}`;

      const forumAPI = new ForumAPI();
      const response = await forumAPI.get(`api/v1/classes/${classId}/get_flight_urls`);
      if (response.status === 404) {
        redirectToNotFound();
        return;
      }
      if (response.ok) {
        const { flight_url, flight_monitoring_url } = await response.json();

        if (!flight_monitoring_url) {
          addAlert({
            id: crypto.randomUUID(),
            content:
              "This class is not assigned to any monitoring server. Monitoring for this class is not available.",
            kind: "warn",
          });
        }
        const flightAPI = new FlightAPI(flight_url, "");
        await flightAPI.getFlags();
        if (isFlagActiveForClass(window.FLAGS, FlagNameType.DISABLE_MONITORING_EVENTS, classId)) {
          addAlert({
            id: crypto.randomUUID(),
            content: "Monitoring events are currently disabled.",
            kind: "warn",
          });
        } else if (
          isFlagActiveForClass(
            window.FLAGS,
            FlagNameType.DISABLE_NON_ESSENTIAL_MONITORING_EVENTS,
            classId,
          )
        ) {
          addAlert({
            id: crypto.randomUUID(),
            content: "Non-essential monitoring events are currently disabled.",
            kind: "warn",
          });
        }

        const reduxPubsubClient = getMonitoringReduxPubsubClient(
          classId,
          flightAuthToken,
          currentUserId,
          flight_url,
          flight_monitoring_url,
        );
        if (!ENVIRONMENT.startsWith("production")) {
          window.flightStore = reduxPubsubClient.store;
        }
        setStore(reduxPubsubClient.store);
      }
    }

    initializeReduxPubsub();
    // addAlert seems to be changing on every render, so we need to disable the eslint rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightAuthToken, classId]);

  useEffect(() => {
    const title = `Flight | Monitoring Dashboard`;
    document.title = title;
    const meta = document.querySelector<HTMLMetaElement>("meta[name='description']");
    if (meta) {
      meta.content = title;
    }
  }, []);

  if (!classId || !store) return null;

  return (
    <ReduxProvider store={store}>
      {userId ? (
        <MonitoringClassUserDashboard classId={classId} userId={userId} />
      ) : (
        <MonitoringClassDashboard />
      )}
    </ReduxProvider>
  );
}

export { MonitoringWrapper };
